// src/components/TemplateWorkspace/PromptsWorkspace.jsx
import React, { useEffect, useRef, useState } from 'react';
import './promptsWorkspace.css';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, db } from '../../../firebase';
import { doc, updateDoc, deleteField } from 'firebase/firestore';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUp,
  faPlus,
  faTimes,
  faCopy,
  faCheck,
} from '@fortawesome/free-solid-svg-icons';

import { generateShortcuts } from '../generateShortcuts';
import ShortCutsTab from './shortCutTab';
import PromptOutput from './promptOutputRender';

const MAX_CHATS = 7;
const MAX_LENGTH = 500;
const MAX_LINES = 5;
const PROCESSING_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes

export default function PromptsWorkspace({
  isTranscriptAvailable,
  isLocalTranscriptAvailable,
  content_id,
  timestamps,
  summary,
  takeaways,
  titles,
  hashtags,
  isScRun,
  isScTimestampsEnabled,
  isScDescriptionEnabled,
  isScTakeawaysEnabled,
  isScTitlesEnabled,
  isScTagsEnabled,

  // lifted state props:
  promptMap,
  outputMap,
  chatNameMap,
  processingMap,
  setPromptMap,
  setOutputMap,
  setChatNameMap,
  setProcessingMap,
}) {
  const functions = getFunctions();
  const generateWorkspaceOutput = httpsCallable(functions, 'generateWorkspaceOutput');
  const textareaRef = useRef(null);

  // UI flags
  const [debouncedMap, setDebouncedMap]         = useState({});
  const [copiedMap, setCopiedMap]               = useState({ "0": { input: false, output: false } });
  const [activeTab, setActiveTab]               = useState("0");
  const [isDisabled, setIsDisabled]             = useState(false);
  const [copiedDetail, setCopiedDetail]         = useState(null);
  const [highlightedTags, setHighlightedTags]   = useState([]);
  const [isReloadDisabled, setIsReloadDisabled] = useState(false);
  const [reloadCooldown, setReloadCooldown]     = useState(0);
  const [showSaveBanner, setShowSaveBanner]     = useState(true);

  // dismiss banner
  useEffect(() => {
    const user = auth.currentUser;
    if (user && localStorage.getItem(`workspaceDisclaimer_${user.uid}`) === 'hidden') {
      setShowSaveBanner(false);
    }
  }, []);

  const dismissSaveBanner = () => {
    const user = auth.currentUser;
    if (user) localStorage.setItem(`workspaceDisclaimer_${user.uid}`, 'hidden');
    setShowSaveBanner(false);
  };

  // disable on `/new`
  useEffect(() => {
    setIsDisabled(window.location.pathname.includes('/new'));
  }, []);

  // auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [promptMap[activeTab], activeTab]);

  // reload cooldown
  useEffect(() => {
    const saved = localStorage.getItem(`reloadCooldownEnd_${content_id}`);
    if (saved) {
      const rem = Math.max(0, Math.floor((+saved - Date.now()) / 1000));
      if (rem > 0) {
        setIsReloadDisabled(true);
        setReloadCooldown(rem);
      }
    }
    let iv;
    if (reloadCooldown > 0) {
      iv = setInterval(() => setReloadCooldown(r => r - 1), 1000);
    } else if (reloadCooldown === 0 && isReloadDisabled) {
      setIsReloadDisabled(false);
      localStorage.removeItem(`reloadCooldownEnd_${content_id}`);
    }
    return () => clearInterval(iv);
  }, [reloadCooldown, isReloadDisabled, content_id]);

  // highlight tags
  useEffect(() => {
    if (Array.isArray(hashtags)) {
      const count = Math.min(5, hashtags.length);
      setHighlightedTags(Array.from({ length: count }, (_, i) => i));
    } else {
      setHighlightedTags([]);
    }
  }, [hashtags]);

  // clear stale processing entries
  useEffect(() => {
    Object.entries(processingMap).forEach(([key, { is_processing, start_timestamp }]) => {
      if (is_processing && start_timestamp) {
        const start = start_timestamp.toMillis
          ? start_timestamp.toMillis()
          : new Date(start_timestamp).getTime();
        if (Date.now() - start > PROCESSING_TIMEOUT_MS) {
          const workspaceRef = doc(
            db,
            'users',
            auth.currentUser.uid,
            'workspaceContent',
            `workspace-${content_id}`
          );
          updateDoc(workspaceRef, {
            [`processing_map.${key}`]: deleteField()
          }).then(() => {
            setProcessingMap(pm => {
              const { [key]: _, ...rest } = pm;
              return rest;
            });
          });
        }
      }
    });
  }, [processingMap, content_id]);

  // **global “any processing” flag**
  const isAnyProcessing = Object.values(processingMap).some(
    ({ is_processing }) => is_processing
  );

  // derive chat keys
  const chatKeys = Array.from(new Set([
    ...Object.keys(chatNameMap),
    ...Object.keys(promptMap),
    ...Object.keys(outputMap),
    ...Object.keys(processingMap),
  ]))
    .filter(k => /^\d+$/.test(k))
    .sort((a, b) => +a - +b);

  // determine if the "Shortcuts" tab should be shown
  const showDetails = (
    isScRun &&
    (isScTimestampsEnabled ||
     isScDescriptionEnabled ||
     isScTakeawaysEnabled ||
     isScTitlesEnabled ||
     isScTagsEnabled)
  );

  // ensure activeTab always points to an existing tab
  useEffect(() => {
    const availableTabs = showDetails
      ? ['details', ...chatKeys]
      : [...chatKeys];

    if (!availableTabs.includes(activeTab)) {
      setActiveTab(availableTabs[0] ?? '');
    }
  }, [chatKeys, showDetails, activeTab]);

  // add chat
  const addChat = () => {
    if (chatKeys.length >= MAX_CHATS) return;
    const next = ((chatKeys.length ? Math.max(...chatKeys.map(n => +n)) + 1 : 0)).toString();
    setPromptMap(pm => ({ ...pm, [next]: "" }));
    setOutputMap(om => ({ ...om, [next]: "" }));
    setChatNameMap(cnm => ({ ...cnm, [next]: "New Chat" }));
    setProcessingMap(pr => ({ ...pr, [next]: { is_processing: false } }));
    setDebouncedMap(dm => ({ ...dm, [next]: false }));
    setCopiedMap(cm => ({ ...cm, [next]: { input: false, output: false } }));
    setActiveTab(next);
  };

  // handle input change – cap length & lines
  const handleInputChange = (idx, rawValue) => {
    if (rawValue.length > MAX_LENGTH) return;
    const lines = rawValue.split('\n');
    let value = rawValue;
    if (lines.length > MAX_LINES) {
      value = lines.slice(0, MAX_LINES).join('\n');
    }
    setPromptMap(pm => ({ ...pm, [idx]: value }));
  };

  // copy prompt/output
  const handleCopy = (idx, type, text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedMap(cm => ({ ...cm, [idx]: { ...cm[idx], [type]: true } }));
      setTimeout(() => setCopiedMap(cm => ({ ...cm, [idx]: { ...cm[idx], [type]: false } })), 3000);
    });
  };

  // copy details for shortcuts
  const handleCopyDetails = (txt, dt) => {
    navigator.clipboard.writeText(txt).then(() => {
      setCopiedDetail(dt);
      setTimeout(() => setCopiedDetail(null), 3000);
    });
  };

  // reload shortcuts
  const handleReload = async () => {
    if (isReloadDisabled) return;
    setIsReloadDisabled(true);
    const dur = 120, end = Date.now() + dur * 1000;
    localStorage.setItem(`reloadCooldownEnd_${content_id}`, end);
    setReloadCooldown(dur);
    try {
      await generateShortcuts(content_id, {
        timestamps: isScTimestampsEnabled,
        description: isScDescriptionEnabled,
        takeaways: isScTakeawaysEnabled,
        titles: isScTitlesEnabled,
        tags: isScTagsEnabled,
        isReprocess: true,
      });
    } catch {
      setIsReloadDisabled(false);
      setReloadCooldown(0);
      localStorage.removeItem(`reloadCooldownEnd_${content_id}`);
    }
  };

  // submit prompt
  const handleWorkspaceOutput = async idx => {
    const prompt = (promptMap[idx] || "").trim();
    const user = auth.currentUser;
    if (!user || !prompt || debouncedMap[idx]) return;

    const workspaceRef = doc(
      db,
      'users',
      user.uid,
      'workspaceContent',
      `workspace-${content_id}`
    );
    const now = new Date();
    await updateDoc(workspaceRef, {
      [`processing_map.${idx}`]: { is_processing: true, start_timestamp: now }
    });
    setProcessingMap(pm => ({
      ...pm,
      [idx]: { is_processing: true, start_timestamp: now }
    }));

    const key = `debounce_${user.uid}_${content_id}_${idx}`;
    localStorage.setItem(key, (Date.now() + 600000).toString());
    setDebouncedMap(dm => ({ ...dm, [idx]: true }));

    try {
      const res = await generateWorkspaceOutput({
        contentId: `workspace-${content_id}`,
        fullPrompt: prompt,
        promptId: idx,
      });
      const result = res.data?.result;
      if (result) {
        setOutputMap(om => ({ ...om, [idx]: result }));
      }
    } catch {
      await updateDoc(workspaceRef, {
        [`processing_map.${idx}`]: deleteField()
      });
      setProcessingMap(pm => {
        const { [idx]: _, ...rest } = pm;
        return rest;
      });
    } finally {
      setDebouncedMap(dm => ({ ...dm, [idx]: false }));
      localStorage.removeItem(key);
    }
  };

  return (
    <div className="workprompt-container">
      {showSaveBanner && (
        <div className="workprompt-saveBanner">
          <span>Heads up! <b>Hit Save</b>—your changes aren’t saved automatically.</span>
          <button onClick={dismissSaveBanner} className="workprompt-saveBanner-close" aria-label="Dismiss">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}

      <div className="workprompt-tabs">
        {showDetails && (
          <button
            key="details"
            className={`workprompt-tabButton ${activeTab === 'details' ? 'active' : ''}`}
            onClick={() => setActiveTab('details')}
          >
            <b>Shortcuts</b>
          </button>
        )}

        {chatKeys.map(key => (
          <button
            key={key}
            className={`workprompt-tabButton ${activeTab === key ? 'active' : ''}`}
            onClick={() => setActiveTab(key)}
          >
            {chatNameMap[key] || `Chat ${key}`}
          </button>
        ))}

        {chatKeys.length < MAX_CHATS && (
          <button
            className="workprompt-tabButton add-button"
            onClick={addChat}
            disabled={
              (!isTranscriptAvailable && !isLocalTranscriptAvailable) ||
              isAnyProcessing
            }
            aria-disabled={
              (!isTranscriptAvailable && !isLocalTranscriptAvailable) ||
              isAnyProcessing
            }
            aria-label="Add Chat"
          >
            <FontAwesomeIcon icon={faPlus} /> Chat
          </button>
        )}
      </div>

      <div className="workprompt-content">
        {activeTab !== 'details'
          ? chatKeys.map(key => activeTab === key && (
              <div className="workprompt-promptBox" key={key}>
                {outputMap[key] ? (
                  <div className="workprompt-readOnlyContainer">
                    <div className="workprompt-readOnlyContent">
                      <div className="workprompt-readOnlyText">{promptMap[key]}</div>
                      <button
                        className="workprompt-copyButton"
                        onClick={() => handleCopy(key, 'input', promptMap[key])}
                        aria-label="Copy Prompt"
                      >
                        {copiedMap[key]?.input
                          ? <FontAwesomeIcon icon={faCheck} className="workprompt-copyIcon copied"/>
                          : <FontAwesomeIcon icon={faCopy} className="workprompt-copyIcon"/>}
                      </button>
                    </div>
                    <PromptOutput
                      promptKey={key}
                      promptOutput={outputMap[key]}
                      isCopiedOutput={copiedMap[key]?.output}
                      handleCopy={(innerKey, text, type) => {
                        handleCopy(key, type, text);
                      }}
                    />
                  </div>
                ) : (
                  <div className="workprompt-promptBoxInner">
                    <div className="workprompt-chatInputWrapper">
                      <div className={`workprompt-input-container ${processingMap[key]?.is_processing ? 'loading' : ''}`}>
                        <textarea
                          ref={textareaRef}
                          className="workprompt-textarea"
                          value={promptMap[key] || ''}
                          onChange={e => handleInputChange(key, e.target.value)}
                          placeholder={
                            processingMap[key]?.is_processing
                              ? 'Bump-1.0 is watching your video...'
                              : isAnyProcessing
                                ? 'Bump-1.0 is currently processing your chat...'
                                : (!isTranscriptAvailable && !isLocalTranscriptAvailable)
                                  ? 'Upload a video to chat'
                                  : 'Message Bump-1.0'
                          }
                          maxLength={MAX_LENGTH}
                          rows={1}
                          disabled={
                            isAnyProcessing ||
                            (!isTranscriptAvailable && !isLocalTranscriptAvailable) ||
                            isDisabled
                          }
                        />
                        {!processingMap[key]?.is_processing ? (
                          <button
                            className="workprompt-submitButton"
                            onClick={() => handleWorkspaceOutput(key)}
                            disabled={
                              !(promptMap[key]||'').trim() ||
                              isDisabled ||
                              (!isTranscriptAvailable && !isLocalTranscriptAvailable) ||
                              debouncedMap[key]
                            }
                            aria-label="Submit Prompt"
                          >
                            <FontAwesomeIcon icon={faArrowUp}/>
                          </button>
                        ) : (
                          <div className="workprompt-spinner" aria-label="Loading…"/>
                        )}
                      </div>
                    </div>
                    <div className="workprompt-charCount">
                      {(promptMap[key]||'').length}/{MAX_LENGTH}
                    </div>
                  </div>
                )}
              </div>
            ))
          : (
            <ShortCutsTab
              isScTimestampsEnabled={isScTimestampsEnabled}
              isScDescriptionEnabled={isScDescriptionEnabled}
              isScTakeawaysEnabled={isScTakeawaysEnabled}
              isScTitlesEnabled={isScTitlesEnabled}
              isScTagsEnabled={isScTagsEnabled}
              timestamps={timestamps}
              summary={summary}
              takeaways={takeaways}
              titles={titles}
              hashtags={hashtags}
              isScRun={isScRun}
              needsReload={
                (isScTimestampsEnabled && !timestamps) ||
                (isScDescriptionEnabled && !summary) ||
                (isScTakeawaysEnabled && takeaways.length === 0) ||
                (isScTitlesEnabled && titles.length === 0) ||
                (isScTagsEnabled && hashtags.length === 0)
              }
              isReloadDisabled={isReloadDisabled}
              reloadCooldown={reloadCooldown}
              handleReload={handleReload}
              handleCopyDetails={handleCopyDetails}
              copiedDetail={copiedDetail}
              highlightedTags={highlightedTags}
            />
          )
        }
        <div className="workprompt-disclaimer">
          Bump-1.0 can make mistakes. Check important info.
        </div>
      </div>
    </div>
  );
}
